import { useCallback } from "react";

import { v4 as uuidv4 } from "uuid";

import { IndustryType, Model, ResultFile } from "./useFile";
import { EndpointType } from "../constants/Endpoints";
import {
  EXTRACTOR_SAMPLES,
  INDUSTRY_TYPE_ORDER,
  LAYOUT_ANALYSIS_SAMPLES,
  OCR_SAMPLES,
} from "../constants/Samples";
import { ImageOacJson } from "../types/demo";
import {
  OCROacJsonResponse,
  convertResponseToImageOCROacJson,
} from "../types/ocrOacResponse";
import { Sample, SampleFileResult } from "../types/sample";

export type SamplesByIndustry = {
  [key in IndustryType]: Sample[];
};

const groupSamplesByIndustry = (samples: Sample[]): SamplesByIndustry => {
  // industryType별로 샘플들을 그룹화
  const groupedMap = samples.reduce((acc, sample) => {
    const type = sample.documentInfo.industryType as IndustryType;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(sample);
    return acc;
  }, {} as SamplesByIndustry);

  // INDUSTRY_TYPE_ORDER 정의된 순서대로 그룹화된 결과 반환
  return INDUSTRY_TYPE_ORDER.filter(
    type => groupedMap[type]?.length > 0,
  ).reduce((acc, industryType) => {
    acc[industryType] = groupedMap[industryType] || [];
    return acc;
  }, {} as SamplesByIndustry);
};

interface FileContextActions {
  setSelectedFileIndex: (index: number) => void;
  setFiles: (files: ResultFile[]) => void;
}

export const useSampleFileLoader = () => {
  const getSamples = useCallback((endpointType: string) => {
    switch (endpointType.toLowerCase()) {
      case "extractor":
        return EXTRACTOR_SAMPLES;
      case "ocr":
        return OCR_SAMPLES;
      case "layout-analysis":
        return LAYOUT_ANALYSIS_SAMPLES;
      default:
        return [];
    }
  }, []);

  const getSamplesGroupedByIndustry = useCallback(
    (endpointType: string): SamplesByIndustry => {
      const samples = getSamples(endpointType);
      return groupSamplesByIndustry(samples);
    },
    [getSamples],
  );

  const getFirstSampleGroupedByIndustry = useCallback(
    (endpointType: string, _samples?: SamplesByIndustry): Sample | null => {
      const samples = _samples
        ? _samples
        : getSamplesGroupedByIndustry(endpointType);
      if (!samples) return null;

      // 첫 번째 industry type의 첫 번째 sample 찾기
      const firstIndustryType = INDUSTRY_TYPE_ORDER.find(
        type => samples[type]?.length > 0,
      );

      if (!firstIndustryType) return null;

      return samples[firstIndustryType][0];
    },
    [],
  );

  const getModelIndex = useCallback(
    (sample: Sample, endpointType: string, models?: Model[]) => {
      switch (endpointType.toLowerCase()) {
        case "extractor":
          return models?.length
            ? models.findIndex(model =>
                sample.documentInfo.modelName.includes(model.name),
              )
            : 0; // model 로드 실패 시 0번 인덱스 반환
        case "ocr":
          return 0; // TODO: OCR 모델 인덱스 추가
        case "layout-analysis":
          return 0; // TODO: LA 모델 인덱스 추가
        default:
          throw new Error("Invalid endpoint type");
      }
    },
    [],
  );

  const fetchSampleFileResult = useCallback(
    async (sample: Sample, endpointType: string) => {
      const response = await fetch(sample.filePath);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      try {
        const result: SampleFileResult = await response.json();

        switch (endpointType.toLowerCase()) {
          case "extractor":
            return result;
          case "ocr":
            return convertResponseToImageOCROacJson(
              result as unknown as OCROacJsonResponse,
            );
          case "layout-analysis":
            return result;
          default:
            return {} as SampleFileResult;
        }
      } catch (error) {
        console.error("json parse error", error);
        return null;
      }
    },
    [],
  );

  const loadSampleFiles = useCallback(
    async (endpointType: string, models?: Model[]) => {
      const samples = getSamples(endpointType);

      try {
        const sampleFiles: ResultFile[] = [];

        // Promise.all을 사용하여 병렬로 파일 로딩
        const results = await Promise.all(
          samples.map(sample => fetchSampleFileResult(sample, endpointType)),
        );

        // 성공적으로 로드된 결과만 ResultFile 배열에 추가
        results.forEach((result, index) => {
          if (result !== null) {
            sampleFiles.push({
              id: uuidv4(),
              title: samples[index].title,
              result: result,
              modelIndex: getModelIndex(samples[index], endpointType, models),
              documentInfo: samples[index].documentInfo,
            });
          }
        });

        return sampleFiles;
      } catch (error) {
        console.error("Error loading sample files:", error);
        return [];
      }
    },
    [fetchSampleFileResult, getSamples, getModelIndex],
  );

  const selectSampleFile = useCallback(
    async ({
      sample,
      endpointType,
      files,
      actions,
    }: {
      sample: Sample;
      endpointType: EndpointType;
      files: ResultFile[];
      actions: FileContextActions;
    }) => {
      const { setSelectedFileIndex, setFiles } = actions;

      // 이미 로드된 파일인지 확인
      const foundIndex = files.findIndex(file => file.id === sample.id);
      if (foundIndex !== -1) {
        setSelectedFileIndex(foundIndex);
        return;
      }

      // 새로운 파일 로드
      const result = (await fetchSampleFileResult(
        sample,
        endpointType,
      )) as unknown as ImageOacJson;

      const newFileIndex = files.length;
      setFiles([
        ...files,
        {
          id: sample.id,
          title: sample.title,
          result: result,
          modelIndex: 0, // TODO : 모델 인덱스 추가
          documentInfo: sample.documentInfo,
        },
      ]);
      setSelectedFileIndex(newFileIndex);
    },
    [fetchSampleFileResult],
  );

  return {
    getSamples,
    getSamplesGroupedByIndustry,
    getFirstSampleGroupedByIndustry,
    loadSampleFiles,
    fetchSampleFileResult,
    selectSampleFile,
  };
};
